import React, { Component, Fragment } from 'react'
import type {
    CategoryWithProductsDTO,
    FilterShortDTO,
    ProductOfCategoryDTO
} from 'shared/types'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import styled from 'styled-components'
import { Filters } from 'app/common/filters'
import {
    breakpoints,
    media,
    ResponsiveContainer,
    style
} from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { categoryId, productFilters, updateFiltersArray } from 'shared/utils'
import { CaptionText, H2 } from 'app/common/typography'
import { ToggleButton } from 'app/common/toggle-button'
import Responsive from 'react-responsive'
import Modal from 'react-modal'
import { ModalTitle } from 'app/common/modals'
import { DropdownButton } from 'app/common/dropdown'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import { MenuItem } from 'app/common/layout/menu'
import { TileSquareGrid } from 'app/common/tile-square'
import { Banner } from './Banner'

const { grey } = colors
const { dropdownModalStyle } = style

type Props = {
    categoryWithProducts: CategoryWithProductsDTO,
    resetCategoryWithProducts: () => void,
    selectedAllergens: Array<string>,
    applySelectedAllergens: (Array<string>) => void,
    sendMeta: EventMetadata => void,
    sendECommerceMeta: ECommerceMetadata => void,
    intl: IntlShape
}

type State = {
    products: Array<ProductOfCategoryDTO>,
    selectedFilters: Array<string>,
    allergens: Array<FilterShortDTO>,
    selectedAllergens: Array<string>,
    showAllergens: boolean
}

class CategoryWithProductsComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        const { categoryWithProducts } = props
        this.state = {
            products: categoryWithProducts.products,
            selectedFilters: [],
            allergens: categoryWithProducts.allergens,
            selectedAllergens: [],
            showAllergens: false
        }
    }

    componentDidMount() {
        const { categoryWithProducts } = this.props
        this.sendImpressionMeta(categoryWithProducts.products)
    }

    componentDidUpdate(prevProps: Props) {
        const { categoryWithProducts } = this.props
        const { products } = categoryWithProducts
        if (prevProps.categoryWithProducts.products !== products) {
            this.sendImpressionMeta(products)
            this.setState({
                products: products
            })
        }
    }

    sendImpressionMeta(products: Array<ProductOfCategoryDTO>) {
        const eCommerceImpressionMetadata = []
        products.forEach((product: ProductOfCategoryDTO, index) =>
            eCommerceImpressionMetadata.push({
                name: product.title,
                position: index
            })
        )
        if (eCommerceImpressionMetadata.length > 0) {
            this.props.sendECommerceMeta({
                type: 'impression',
                data: eCommerceImpressionMetadata
            })
        }
    }

    componentWillUnmount() {
        this.props.resetCategoryWithProducts()
    }

    filterProducts = () => {
        const allProducts = this.props.categoryWithProducts.products
        const selectedAllergens = this.state.selectedAllergens
        const productsByAllergens = allProducts.filter(
            (p: ProductOfCategoryDTO) =>
                p.allergens.every((a: string) =>
                    selectedAllergens.every((sa: string) => a !== sa)
                )
        )

        const selectedFilters = this.state.selectedFilters
        const products = productsByAllergens.filter((p: ProductOfCategoryDTO) =>
            selectedFilters.every((sf: string) =>
                p.contains.some((f: string) => sf === f)
            )
        )
        this.setState({ products })
    }

    applyAllergens = (selectedAllergens: Array<string>) => {
        const { applySelectedAllergens } = this.props
        applySelectedAllergens(selectedAllergens)
        this.filterProducts()
    }

    filterByComposition = (filter: FilterShortDTO) => {
        const { categoryWithProducts } = this.props
        const { selectedFilters } = this.state
        const { categoryInfos } = categoryWithProducts
        let meta = { ...eventsDefinitions['CATEGORY_FILTER'] }
        const categoryId = categoryInfos.categoryId
        if (selectedFilters.includes(filter.code)) {
            meta.event_label = filter.code + ' : Désélectionné'
        } else {
            meta.event_label = filter.code + ' : Sélectionné'
        }
        meta.event_category = 'Carte - ' + categoryId
        this.props.sendMeta(meta)

        const newSelectedFilters = updateFiltersArray(
            [...selectedFilters],
            filter.code,
            true
        )
        this.setState(
            { selectedFilters: newSelectedFilters },
            this.filterProducts
        )
    }

    toggleAllergens = () => {
        const showAllergens = !this.state.showAllergens
        this.setState({ showAllergens })
    }

    displayDropdown = () => {
        if (this.state.showAllergens) {
            return
        }
        this.setState({ showAllergens: true })
    }

    updateAllergens = (allergen: FilterShortDTO) => {
        const selectedAllergens = updateFiltersArray(
            [...this.state.selectedAllergens],
            allergen.code
        )
        this.setState({ selectedAllergens })
    }

    resetAllergens = () => {
        if (this.state.selectedAllergens.length === 0) {
            return
        }
        this.setState({ selectedAllergens: [] }, () => this.applyAllergens([]))
    }

    filterByAllergens = () => {
        this.applyAllergens(this.state.selectedAllergens)
        this.toggleAllergens()
    }

    render() {
        const { intl, categoryWithProducts } = this.props
        const catalogRoute = intl.formatMessage({ id: 'routes.catalog' })
        const {
            categoryInfos,
            filters,
            allergens,
            banner
        } = categoryWithProducts

        const breadcrumb = [
            {
                link: catalogRoute,
                title: intl.formatMessage({
                    id: 'catalog.card.title.breadcrumb.end'
                })
            },
            {
                title: `\u00A0>\u00A0${categoryInfos.title}`
            }
        ]
        const description = categoryInfos.description
        const breakpoint = breakpoints.medium
        const {
            products,
            selectedAllergens,
            selectedFilters,
            showAllergens
        } = this.state

        const cleanFilters = filters.filter(
            filter =>
                !(
                    [categoryId.DRINKS, categoryId.DESSERTS].includes(
                        categoryInfos.categoryId
                    ) && filter.code === productFilters.NO_BACON
                )
        )

        const tiles = products.map(product => {
            const meta = { ...eventsDefinitions['CATEGORY_PRODUCT_CLICK'] }
            meta.event_category += categoryInfos.title
            meta.event_action = product.title

            return {
                actionUrl: `${product.productId}`,
                image: product.image,
                name: product.title,
                /*DO NOT DELETE
                price: product.price ? product.price : null,
                backgroundColor: product.backgroundColor ? product.backgroundColor : colors.tertiary,*/
                contains: product.contains,
                allergens: product.allergens,
                color: 'black',
                type: 'product',
                meta: meta
                /*
                DO NOT DELETE => ICON BOTTOM
                ,iconBottom: (
                    <CirclePlus color={'white'} background={colors.red} />
                )*/
            }
        })

        return (
            <MenuItem
                breadcrumb={breadcrumb}
                description={description}
                url={catalogRoute}
                title={categoryInfos.title}>
                {banner && <Banner {...banner} />}
                <FiltersContainer>
                    {cleanFilters.length > 0 && (
                        <CaptionText color={grey}>
                            <FormattedMessage
                                id="catalog.filters.label"
                                values={{
                                    elements: categoryInfos.title
                                        .toString()
                                        .toLowerCase()
                                }}
                            />
                        </CaptionText>
                    )}
                    <FilterZone>
                        {filters && (
                            <FiltersToggle>
                                {cleanFilters.map((filter, index) => (
                                    <ToggleButton
                                        active={
                                            selectedFilters.indexOf(
                                                filter.code
                                            ) > -1
                                        }
                                        key={index}
                                        {...filter}
                                        handleClick={() =>
                                            this.filterByComposition(filter)
                                        }
                                        marginBottom
                                    />
                                ))}
                            </FiltersToggle>
                        )}
                        {allergens && (
                            <StyledDropdownButtonContainer
                                onClick={this.displayDropdown}>
                                <DropdownButton
                                    label={intl.formatMessage({
                                        id: 'catalog.filters.by.label'
                                    })}
                                    collapsed={showAllergens}>
                                    <Fragment>
                                        <Responsive maxWidth={breakpoint - 1}>
                                            <Modal
                                                isOpen={showAllergens}
                                                ariaHideApp={false}
                                                style={dropdownModalStyle}>
                                                <ResponsiveContainer>
                                                    <ModalTitle
                                                        titleNode={
                                                            <StyledH2>
                                                                {intl.formatMessage(
                                                                    {
                                                                        id:
                                                                            'modal.filters.title'
                                                                    }
                                                                )}
                                                            </StyledH2>
                                                        }
                                                        handleClose={
                                                            this.toggleAllergens
                                                        }
                                                    />
                                                    <Filters
                                                        filters={allergens}
                                                        selectedFilters={
                                                            selectedAllergens
                                                        }
                                                        handleReset={
                                                            this.resetAllergens
                                                        }
                                                        handleApply={
                                                            this
                                                                .filterByAllergens
                                                        }
                                                        handleClose={
                                                            this.toggleAllergens
                                                        }
                                                        handleUpdate={allergen =>
                                                            this.updateAllergens(
                                                                allergen
                                                            )
                                                        }
                                                    />
                                                </ResponsiveContainer>
                                            </Modal>
                                        </Responsive>
                                        <Responsive minWidth={breakpoint}>
                                            {showAllergens && (
                                                <Filters
                                                    clickOutside
                                                    filters={allergens}
                                                    selectedFilters={
                                                        selectedAllergens
                                                    }
                                                    handleReset={
                                                        this.resetAllergens
                                                    }
                                                    handleApply={
                                                        this.filterByAllergens
                                                    }
                                                    handleClose={
                                                        this.toggleAllergens
                                                    }
                                                    handleUpdate={allergen =>
                                                        this.updateAllergens(
                                                            allergen
                                                        )
                                                    }
                                                />
                                            )}
                                        </Responsive>
                                    </Fragment>
                                </DropdownButton>
                            </StyledDropdownButtonContainer>
                        )}
                    </FilterZone>
                </FiltersContainer>
                <TileSquareContainer>
                    <TileSquareGrid justify="flex-start" tiles={tiles} />
                </TileSquareContainer>
            </MenuItem>
        )
    }
}

const FiltersContainer = styled.div`
    width: 100%;
    margin-top: 15px;
    ${media.medium`
        margin-top: 40px;
        width: calc(100% - 25px);
    `};
    ${media.large`
        width: calc(100% - 40px);
    `};
`

const FilterZone = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    margin-top: 10px;
    ${media.medium`
        flex-direction: row;
    `};
`

const FiltersToggle = styled.div`
    width: 100%;
    white-space: nowrap;
    overflow-x: scroll;
    margin: 0 -20px 10px -20px;
    padding-left: 20px;
    display: flex;
    align-items: center;
    ::-webkit-scrollbar {
        display: none;
    }
    ${media.medium`
        margin: 0;
        padding-left: 0;
        flex-wrap: wrap;
    `};
`

const StyledDropdownButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
    position: relative;
    width: 100%;
    ${media.medium`
        justify-content: flex-end;
        width: 30%;
    `};
`

const TileSquareContainer = styled.div`
    width: 100%;
    margin-top: 40px;
    min-height: 400px;
    ${media.medium`
        min-height: 900px;
    `};
`

const StyledH2 = styled(H2)`
    margin: 0;
`

const CategoryWithProducts = injectIntl(CategoryWithProductsComponent)

export { CategoryWithProducts }
